import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Button } from "primereact/button";
import { useTranslation, Trans } from "react-i18next";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Paginator } from "primereact/paginator";
import Loading from "../../Components/Loading";
import DefMembershipActions from "../../Redux/actions/defMembership-actions";
import BusinessCustomersActions from "../../Redux/transition/customers/business-customer.reducer";
import CustomDataTable from "../../Components/CustomDataTable";
import CustomToast from "../../Components/CustomToast";
import InviteSubscriber from "../Subscriptions/Invitation/InviteSubscriber";
import SubscriberToSegment from "./Segments/subscriberToSegment";
import AudienceDetailCards from "./AudienceDetailCards";
import AddAudienceMemberDialog from "./AddAudienceMemberDialog";
import SpecifyCriteriaDialog from "./SpecifyCriteriaDialog";
import RemoveAudienceMemberDialog from "./RemoveAudienceMemberDialog";
import AudienceCsvEntriesAccordion from "./AudienceCsvEntriesAccordion";
import AudienceMemberTagsCell from "./AudienceMemberTagsCell";
import { hasAnyCompletedBatch, isBatchCompleted } from "./audienceBatchProgress";
import { useDateFormatter } from "../../Components/FormatDateTime";
import { FormatCurrency, useCurrencyConstants } from "../../Components/CurrencyFormatter";
import { formatBillingPeriodText, removePlusFromNum } from "../../Components/CleanInputs";
import "../../Styles/pages/customers.scss";
import "../../Styles/pages/product.scss";
import "../../Styles/pages/products.scss";
import "../../Styles/dataTable.scss";

const AUDIENCE_DETAIL_MOBILE_BREAKPOINT = 1050;

const withProductTableHeader = (column) => ({
    ...column,
    headerClassName: column.headerClassName ?? "datatable-header-default",
});

const AudienceDetail = () => {
    const { formatDateTime } = useDateFormatter();
    const { currencyFormat, currencySymbol } = useCurrencyConstants();
    const { t } = useTranslation("common");
    const { id: audienceId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [activeTab, setActiveTab] = useState(0);
    const [tableState, setTableState] = useState({ page: 0, first: 0, tab: false });
    const [visibleInvite, setVisibleInvite] = useState(false);
    const [showSegmentPopup, setShowSegmentPopup] = useState(false);
    const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
    const [showCriteriaDialog, setShowCriteriaDialog] = useState(false);
    const [showRemoveMemberDialog, setShowRemoveMemberDialog] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [detectClicked, setDetectClicked] = useState(false);
    const [resendVisible, setResendVisible] = useState(false);
    const [resendId, setResendId] = useState(null);
    const [resendClicked, setResendClicked] = useState(false);
    const [applying, setApplyingLocal] = useState(false);
    const [refreshingCsvAfterApply, setRefreshingCsvAfterApply] = useState(false);
    const [toastTrigger, setToastTrigger] = useState(false);
    const [toastData, setToastData] = useState({ summary: "", severity: "", detail: "", life: 0 });
    const [membersPage, setMembersPage] = useState({ page: 0, size: 20, sort: "id,asc" });
    const [audienceCsvDownloadRequested, setAudienceCsvDownloadRequested] = useState(false);
    const [isMobileStack, setIsMobileStack] = useState(
        () => typeof window !== "undefined" && window.innerWidth <= AUDIENCE_DETAIL_MOBILE_BREAKPOINT
    );

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const {
        audience,
        fetchingAudience,
        audienceError,
        audienceMembers,
        fetchingAudienceMembers,
        entries,
        fetchingEntries,
        audienceBatches,
        fetchingAudienceBatches,
        applying: applyingImport,
        applyingBatchNos = [],
        appliedImport,
        appliedImportError,
        customerListSuccess: listPlans,
        fetchingCustomerListInvitation: fetchingListPlans,
        bulkPlanInvite,
        fetchingBulkPlanInvite,
        bulkPlanInviteResend,
        bulkPlanInviteResendFailure,
        fetchingBulkPlanInviteResend,
        audienceCandidateSegments,
        audienceCandidateTags,
        fetchingAudienceCandidateSegments,
        fetchingAudienceCandidateTags,
        applyingAudienceCandidates,
        audienceCandidatesApplied,
        applyAudienceCandidatesError,
        downloadingAudienceMembersCsv,
        downloadAudienceMembersCsvError,
    } = useSelector((state) => state.businessCustomers);

    const dispatch = useDispatch();
    const getMembership = useCallback(() => dispatch(DefMembershipActions.defMembershipRequest()), [dispatch]);
    const getAudience = useCallback((id) => dispatch(BusinessCustomersActions.audienceRequest(id)), [dispatch]);
    const getAudienceMembers = useCallback(
        (id, options) => dispatch(BusinessCustomersActions.audienceMembersRequest(id, options)),
        [dispatch]
    );
    const getEntries = useCallback((importId) => dispatch(BusinessCustomersActions.businessCustomerEntryRequest(importId)), [dispatch]);
    const getAudienceBatches = useCallback(
        (id) => dispatch(BusinessCustomersActions.audienceBatchesRequest(id)),
        [dispatch]
    );
    const applyImport = useCallback(
        (id, batchNo) => dispatch(BusinessCustomersActions.businessCustomerApplyRequest(id, batchNo)),
        [dispatch]
    );
    const sendBulkPlanInvites = useCallback(
        (businessId, inviteData) => dispatch(BusinessCustomersActions.businessCustomerBulkPlanInviteRequest(businessId, inviteData)),
        [dispatch]
    );
    const resendBulkPlanInvites = useCallback(
        (id) => dispatch(BusinessCustomersActions.businessCustomerBulkResendInvitationRequest(id)),
        [dispatch]
    );
    const getCustomerListInvitedPlans = useCallback(
        (targetListId) => dispatch(BusinessCustomersActions.businessCustomerBulkInvitationRequest(targetListId)),
        [dispatch]
    );
    const getAudienceCandidateSegments = useCallback(
        (id) => dispatch(BusinessCustomersActions.audienceCandidateSegmentsRequest(id)),
        [dispatch]
    );
    const getAudienceCandidateTags = useCallback(
        (id) => dispatch(BusinessCustomersActions.audienceCandidateTagsRequest(id)),
        [dispatch]
    );
    const applyAudienceCandidates = useCallback(
        (id) => dispatch(BusinessCustomersActions.applyAudienceCandidatesRequest(id)),
        [dispatch]
    );
    const downloadAudienceMembersCsv = useCallback(
        (id, filename) => dispatch(BusinessCustomersActions.downloadAudienceMembersCsvRequest(id, filename)),
        [dispatch]
    );

    const audienceData = audience || location.state?.audience || null;
    const canInviteOrSegment = hasAnyCompletedBatch(audienceBatches)
        || (audienceMembers?.totalElements ?? audienceData?.memberCount ?? 0) > 0;
    const candidateSegments = useMemo(
        () => (Array.isArray(audienceCandidateSegments) ? audienceCandidateSegments : []),
        [audienceCandidateSegments]
    );
    const candidateTags = useMemo(
        () => (Array.isArray(audienceCandidateTags) ? audienceCandidateTags : []),
        [audienceCandidateTags]
    );
    const hasMembershipCriteria = candidateSegments.length > 0 || candidateTags.length > 0;

    const fetchAudienceCriteria = useCallback(() => {
        if (!audienceId) {
            return;
        }
        getAudienceCandidateSegments(audienceId);
        getAudienceCandidateTags(audienceId);
    }, [audienceId, getAudienceCandidateSegments, getAudienceCandidateTags]);

    const fetchAudienceMembers = useCallback(() => {
        if (!audienceId) {
            return;
        }
        getAudienceMembers(audienceId, {
            page: membersPage.page,
            size: membersPage.size,
            sort: membersPage.sort,
        });
    }, [audienceId, getAudienceMembers, membersPage.page, membersPage.size, membersPage.sort]);

    useEffect(() => {
        if (!defMembership) {
            getMembership();
            return;
        }
        getAudience(audienceId);
        fetchAudienceCriteria();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audienceId, defMembership]);

    useEffect(() => {
        if (!defMembership || !audienceId) {
            return;
        }
        getAudienceBatches(audienceId);
    }, [defMembership, audienceId, getAudienceBatches]);

    useEffect(() => {
        if (!defMembership || !audienceId || activeTab !== 1) {
            return;
        }
        getEntries(audienceId);
    }, [defMembership, audienceId, activeTab, getEntries]);

    useEffect(() => {
        if (!defMembership || !audienceId || activeTab !== 2) {
            return;
        }
        getCustomerListInvitedPlans(audienceId);
    }, [defMembership, audienceId, activeTab, getCustomerListInvitedPlans]);

    useEffect(() => {
        if (!defMembership || !audienceId) {
            return;
        }
        fetchAudienceMembers();
    }, [defMembership, audienceId, fetchAudienceMembers]);

    useEffect(() => {
        const mediaQuery = window.matchMedia(`(max-width: ${AUDIENCE_DETAIL_MOBILE_BREAKPOINT}px)`);
        const onBreakpointChange = (event) => setIsMobileStack(event.matches);
        onBreakpointChange(mediaQuery);
        mediaQuery.addEventListener("change", onBreakpointChange);
        return () => mediaQuery.removeEventListener("change", onBreakpointChange);
    }, []);

    const showToast = (summary, severity, detail, life = 3000) => {
        setToastTrigger(false);
        setToastData({ summary, severity, detail, life });
        window.requestAnimationFrame(() => setToastTrigger(true));
    };

    const handleResendSuccessAfterToast = () => {
        setToastTrigger(false);
        setApplyingLocal(false);
        if (!resendClicked) {
            return;
        }
        setResendId(null);
        setResendVisible(false);
        setResendClicked(false);
        getCustomerListInvitedPlans(audienceId);
    };

    useEffect(() => {
        if (refreshingCsvAfterApply && !fetchingEntries && !fetchingAudienceBatches) {
            setRefreshingCsvAfterApply(false);
        }
    }, [refreshingCsvAfterApply, fetchingEntries, fetchingAudienceBatches]);

    useEffect(() => {
        if (!applyingImport && appliedImport && applying) {
            setRefreshingCsvAfterApply(true);
            getEntries(audienceId);
            getAudienceBatches(audienceId);
            setApplyingLocal(false);
        }
        if (!applyingImport && appliedImportError && applying) {
            showToast(t("err"), "error", t("business_customers.apply_error"));
            setApplyingLocal(false);
        }
        if (!fetchingBulkPlanInviteResend && bulkPlanInviteResend && resendClicked) {
            showToast(t("success"), "success", t("business_customers.resend_sms"));
        }
        if (!fetchingBulkPlanInviteResend && bulkPlanInviteResendFailure && resendClicked) {
            showToast(t("err"), "error", t("business_customers.resend_fail"));
        }
        if (!applyingAudienceCandidates && audienceCandidatesApplied && detectClicked) {
            const processed = audienceCandidatesApplied?.processed ?? audienceCandidatesApplied?.count ?? 0;
            showToast(
                t("success"),
                "success",
                t("business_customers.detect_and_add_result", { count: processed })
            );
            getAudience(audienceId);
            fetchAudienceMembers();
            setDetectClicked(false);
        }
        if (!applyingAudienceCandidates && applyAudienceCandidatesError && detectClicked) {
            showToast(t("err"), "error", t("business_customers.detect_and_add_error"));
            setDetectClicked(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applyingImport, appliedImport, appliedImportError, applying, bulkPlanInviteResend, bulkPlanInviteResendFailure, fetchingBulkPlanInviteResend, resendClicked, applyingAudienceCandidates, audienceCandidatesApplied, applyAudienceCandidatesError, detectClicked]);

    useEffect(() => {
        if (!audienceCsvDownloadRequested || downloadingAudienceMembersCsv) {
            return;
        }

        if (downloadAudienceMembersCsvError) {
            showToast(t("err"), "error", t("business_customers.csv_error"));
        }

        setAudienceCsvDownloadRequested(false);
    }, [audienceCsvDownloadRequested, downloadingAudienceMembersCsv, downloadAudienceMembersCsvError, t]);

    const handleDetectAndAdd = () => {
        setDetectClicked(true);
        applyAudienceCandidates(audienceId);
    };

    const getAudienceMembersCsvFilename = () => {
        const audienceName = audienceData?.name?.trim();
        if (audienceName) {
            const safeName = audienceName.replace(/[^\w-]+/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "");
            if (safeName) {
                return `${safeName}_members.csv`;
            }
        }
        return `audience_${audienceId}_members.csv`;
    };

    const handleDownloadAudienceMembers = () => {
        setAudienceCsvDownloadRequested(true);
        downloadAudienceMembersCsv(audienceId, getAudienceMembersCsvFilename());
    };

    const handleApplyBatch = (batchNo) => {
        if (batchNo == null || batchNo === "") {
            showToast(t("err"), "error", t("business_customers.batch_required"));
            return;
        }

        if (isBatchCompleted(audienceBatches, batchNo)) {
            showToast(t("info"), "info", t("business_customers.batch_already_applied", { batchNo }));
            return;
        }

        showToast(t("info"), "info", t("business_customers.process_batch_started", { batchNo }));
        setApplyingLocal(true);
        applyImport(audienceId, batchNo);
    };

    const handleResend = () => {
        setResendClicked(true);
        resendBulkPlanInvites(resendId);
    };

    const handleRefreshPlanInvitations = () => {
        getCustomerListInvitedPlans(audienceId);
    };

    const boolenConvert = (data) => (data ? t("common.true") : t("common.false"));

    const formatEntryValue = (value) => {
        if (value == null || value === "") {
            return "";
        }
        if (typeof value === "object") {
            if (Array.isArray(value)) {
                return value
                    .map((item) => (typeof item === "object" ? item?.name ?? item?.label : item))
                    .filter(Boolean)
                    .join(", ");
            }
            return value?.name ?? value?.label ?? "";
        }
        return value;
    };

    const entriesBody = (data) => <div className="text-left">{formatEntryValue(data)}</div>;

    const importedEntries = Array.isArray(entries) ? entries : (entries?.content ?? []);
    const planInvitations = Array.isArray(listPlans) ? listPlans : [];

    const memberRows = useMemo(() => {
        const content = audienceMembers?.content ?? [];
        return content.map((member) => {
            const bc = member.businessCustomer || {};
            const entry = member.entry || {};
            return {
                memberId: member.id,
                businessCustomerId: bc.id,
                id: bc.id ?? member.id,
                name: bc.name,
                email: bc.email,
                segment: bc.segment ?? entry.segment,
                tags: bc.tags ?? entry.tags,
                phoneNo: removePlusFromNum(bc.phoneNo ?? entry.phoneNo),
                customerNum: bc.customerNum ?? entry.customerNum,
                reference: bc.reference ?? entry.reference ?? bc.customerNum,
                phoneNoValid: bc.phoneNoValid ?? entry.phoneNoValid,
                imported: entry.imported ?? bc.imported,
            };
        });
    }, [audienceMembers]);

    const existingMemberBcIds = useMemo(
        () => memberRows.map((row) => row.businessCustomerId).filter(Boolean),
        [memberRows]
    );

    const initialCriteriaSegmentIds = useMemo(
        () => candidateSegments.map((item) => item.segment?.id).filter(Boolean),
        [candidateSegments]
    );

    const initialCriteriaTagIds = useMemo(
        () => candidateTags.map((item) => item.tag?.id).filter(Boolean),
        [candidateTags]
    );

    const navigateToSubscriber = (businessCustomerId) => {
        if (businessCustomerId) {
            navigate(`/crm/customers/customer/${businessCustomerId}`);
        }
    };

    const displayMemberCount = audienceMembers?.totalElements ?? audienceData?.memberCount ?? 0;

    const memberIdBody = (row) => (
        <div className="customer_main">
            <span
                className="customerReference"
                style={{ cursor: row.businessCustomerId ? "pointer" : "default" }}
                onClick={() => navigateToSubscriber(row.businessCustomerId)}
            >
                {row.businessCustomerId ?? "--"}
            </span>
            {row.reference ? (
                <span
                    className="customerId"
                    style={{ fontSize: "12px" }}
                    onClick={() => navigateToSubscriber(row.businessCustomerId)}
                >
                    {row.reference}
                </span>
            ) : null}
        </div>
    );

    const memberCustomerNumBody = (row) => (
        <div className="text-left">
            {row.customerNum ? <span className="customerReference">{row.customerNum}</span> : null}
        </div>
    );

    const memberNameBody = (row) => (
        <div className="text-left">
            {row.name ? <span className="customer_name">{row.name}</span> : null}
        </div>
    );

    const memberPhoneBody = (row) => (
        <div className="text-left">
            {row.phoneNo ? <span className="customer_data reveknew_red font-bold">{row.phoneNo}</span> : null}
        </div>
    );

    const memberSegmentBody = (row) => {
        const label = row.segment?.name ?? formatEntryValue(row.segment);
        if (!label) {
            return <div className="text-left" />;
        }
        return (
            <div className="text-left segment-tag-div mb-2" style={{ paddingTop: "0.2rem" }}>
                <p className="detail-navigation segment-class space-beneath blue-text">{label}</p>
            </div>
        );
    };

    const memberTagsBody = (row) => (
        <AudienceMemberTagsCell businessCustomerId={row.businessCustomerId} />
    );

    const memberActionsBody = (row) => (
        <div className="audience-member-actions">
            <p
                className="view-list mb-0"
                onClick={() => {
                    setSelectedMember(row);
                    setShowRemoveMemberDialog(true);
                }}
            >
                {t("business_customers.audience_remove_member_short")}
            </p>
        </div>
    );

    const columnsMembers = [
        { field: "id", header: t("common.id"), body: memberIdBody, alignHeader: "start" },
        { field: "name", header: t("common.name"), body: memberNameBody, alignHeader: "start" },
        { field: "customerNum", header: t("common.reference"), body: memberCustomerNumBody, alignHeader: "start" },
        { field: "phoneNo", header: t("common.phone_no"), body: memberPhoneBody, alignHeader: "start" },
        { field: "segment", header: t("business_customers.segment"), body: memberSegmentBody, alignHeader: "start" },
        { field: "tags", header: t("business_customers.tags"), body: memberTagsBody, alignHeader: "start" },
        { field: "actions", header: "", body: memberActionsBody, alignHeader: "start" },
    ].map(withProductTableHeader);

    const columnsEntries = [
        { field: "name", header: t("common.name"), body: (data) => entriesBody(data.name) },
        { field: "segment", header: t("business_customers.segment"), body: (data) => entriesBody(data.segment) },
        { field: "tags", header: t("business_customers.tags"), body: (data) => entriesBody(data.tags) },
        { field: "phoneNo", header: t("common.phone_no"), body: (data) => entriesBody(data.phoneNo) },
        { field: "customerNum", header: t("common.reference"), body: (data) => entriesBody(data.customerNum) },
        { field: "phoneNoValid", header: t("business_customers.phone_valid"), body: (data) => entriesBody(boolenConvert(data.phoneNoValid)) },
        { field: "imported", header: t("business_customers.imported"), body: (data) => entriesBody(boolenConvert(data.imported)) },
        { field: "batchNo", header: t("business_customers.batch_number"), body: (data) => entriesBody(data.batchNo) },
    ].map(withProductTableHeader);

    const columnsEntriesInBatch = columnsEntries.filter((col) => col.field !== "batchNo");

    const rowExpansionPlanBody = (data) => {
        const requestData = data?.request?.plan;
        return (
            <div className="text-left">
                <p className="text-left policy mb-0">
                    {requestData?.policy === "TIER"
                        ? t("products.fixed_billing").toUpperCase()
                        : requestData?.policy === "SUBSCRIPTION"
                            ? t("products.custom_billing").toUpperCase()
                            : t("billing", { val: requestData?.policy })}
                </p>
                <p id="plan_name" className="mb-0">{requestData?.name}</p>
                <p className="plan-desc mb-0">{requestData?.description}</p>
                <div className="mb-0">
                    <span className="planCost mt-0">
                        {requestData?.policy === "SUBSCRIPTION" && !requestData.cost
                            ? t("amount_fixed")
                            : requestData?.policy === "FLEXIBLE"
                                ? t("products.flexible-amount")
                                : requestData?.deductions !== null
                                    ? (
                                        <Trans
                                            i18nKey="business_customers.deduct"
                                            values={{ deduction: requestData.deductions, currency: currencySymbol, cost: requestData?.cost }}
                                            components={{ 1: <span className="main-text" /> }}
                                        />
                                    )
                                    : (
                                        <FormatCurrency
                                            amount={requestData?.cost}
                                            currencySymbol={currencySymbol}
                                            currencyFormat={currencyFormat}
                                            className=""
                                        />
                                    )}
                    </span>
                    <span className="planBillingPeriod pb-2 pt-0">
                        {formatBillingPeriodText(requestData?.billingPeriod)}
                    </span>
                </div>
            </div>
        );
    };

    const rowExpansionInviteBody = (data) => {
        const requestData = data?.request;
        return (
            <div className="text-left">
                <p className="reveknew_gray text-sm mb-1 font-bold">{t("business_customers.create_on")}</p>
                <span className="products-labelOnDate mb-3">{formatDateTime(requestData?.createdOn)}</span>
                <p className="reveknew_black text-sm mb-0 font-bold">{t("business_customers.latest_invite")}</p>
                {requestData?.completedOn ? (
                    <>
                        <p className="reveknew_green text-sm mb-1 font-bold">{t("business_customers.complete_on")}</p>
                        <span className="products-labelOnDate mb-3">{formatDateTime(requestData?.completedOn)}</span>
                    </>
                ) : (
                    <p className="reveknew_yellow text-sm mb-1 font-bold">{t("business_customers.processing")}</p>
                )}
                <div className="flex">
                    <p className="view-list" onClick={() => { setResendId(data.id); setResendVisible(true); }}>
                        {t("business_customers.resend")}
                    </p>
                </div>
            </div>
        );
    };

    const GridData = ({ dataType = 0, dataText = "", className = "", commonName = "", commonNameMulti = "" }) => (
        <div className="pr-0 pl-0">
            <p className={`mb-0 text-styles-${className}`}>{dataText}</p>
            <span>
                <Trans
                    i18nKey={dataType !== 1 ? `business_customers.${commonNameMulti}` : `business_customers.${commonName}`}
                    values={{ value: dataType }}
                    components={{ a: <span className="invite" /> }}
                />
            </span>
        </div>
    );

    const rowExpansionSubscriptionResultsBody = (data) => {
        const showData = [
            { id: 1, dataText: t("business_customers.active"), dataType: data?.subscrActive, className: "active", commonName: "subscription_one", commonNameMulti: "subscriptions" },
            { id: 2, dataText: t("business_customers.completed_"), dataType: data?.subscrComplete, className: "completed_", commonName: "subscription_one", commonNameMulti: "subscriptions" },
            { id: 3, dataText: t("business_customers.paused"), dataType: data?.subscrPaused, className: "paused", commonName: "subscription_one", commonNameMulti: "subscriptions" },
            { id: 4, dataText: t("business_customers.cancelled"), dataType: data?.subscrCancelled, className: "cancelled", commonName: "subscription_one", commonNameMulti: "subscriptions" },
            { id: 5, dataText: t("failed"), dataType: data?.subscrFailed, className: "fail", commonName: "subscription_one", commonNameMulti: "subscriptions" },
        ];
        return (
            <div className="text-left text-styles">
                {showData.map((item) => <GridData key={item.id} {...item} />)}
            </div>
        );
    };

    const rowExpansionInvitationResultsBody = (data) => {
        const showData = [
            { id: 1, dataText: t("invitation.pending_link"), dataType: data?.pending, className: "paused", commonName: "invite_one", commonNameMulti: "invites" },
            { id: 2, dataText: t("invitation.accepted_invitations"), dataType: data?.accepted, className: "active", commonName: "invite_one", commonNameMulti: "invites" },
        ];
        return (
            <div className="text-left text-styles">
                {showData.map((item) => <GridData key={item.id} {...item} />)}
            </div>
        );
    };

    const rowExpansionResultBody = (data) => {
        const showData = [
            { id: 1, dataText: t("delivered"), dataType: data?.dlrDelivered, className: "delivery", commonName: "message_one", commonNameMulti: "messages" },
            { id: 2, dataText: t("sent"), dataType: data?.dlrSent, className: "sent", commonName: "message_one", commonNameMulti: "messages" },
            { id: 3, dataText: t("queued"), dataType: data?.dlrQueued, className: "queue", commonName: "message_one", commonNameMulti: "messages" },
            { id: 4, dataText: t("failed"), dataType: data?.dlrFailed, className: "fail", commonName: "message_one", commonNameMulti: "messages" },
        ];
        return (
            <div className="text-left">
                <div className="text-styles">
                    {showData.map((item) => <GridData key={item.id} {...item} />)}
                </div>
            </div>
        );
    };

    const planInvitationColumns = [
        { field: "", header: t("business_customers.invite-create"), body: rowExpansionInviteBody, alignHeader: "start" },
        { field: "", header: t("business_customers.plan"), body: rowExpansionPlanBody, alignHeader: "start" },
        { field: "", header: t("business_customers.result"), body: rowExpansionResultBody, alignHeader: "start" },
        { field: "", header: t("business_customers.invitation_result"), body: rowExpansionInvitationResultsBody, alignHeader: "start" },
        { field: "", header: t("business_customers.subscription_result"), body: rowExpansionSubscriptionResultsBody, alignHeader: "start" },
    ].map(withProductTableHeader);

    const renderMobileSummary = (columns, rowData) => (
        <div className="audience-detail-mobile-summary">
            {columns.map((col, index) => {
                const isLast = index === columns.length - 1;
                const isActions = col.field === "actions";
                return (
                    <div
                        key={`${col.field || col.header}-${index}`}
                        className={`audience-detail-mobile-section${isLast ? " audience-detail-mobile-section-last" : ""}${isActions ? " audience-detail-mobile-actions" : ""}`}
                    >
                        {col.header ? (
                            <span className="audience-detail-mobile-label">{col.header}</span>
                        ) : null}
                        {col.body(rowData)}
                    </div>
                );
            })}
        </div>
    );

    const membersMobileSummary = (rowData) => renderMobileSummary(columnsMembers, rowData);
    const planInvitationsMobileSummary = (rowData) => renderMobileSummary(planInvitationColumns, rowData);

    const sideMenu = [
        {
            label: t("business_customers.audience_import_members"),
            icon: "pi-upload",
            show: true,
            onclick: () => navigate(`/crm/audiences/audience/${audienceId}/imports`),
        },
        {
            label: t("business_customers.audience_download_members"),
            icon: "pi-download",
            show: displayMemberCount > 0,
            onclick: handleDownloadAudienceMembers,
            loading: downloadingAudienceMembersCsv,
        },
        {
            label: t("business_customers.specify_criteria"),
            icon: "pi-filter",
            show: true,
            onclick: () => setShowCriteriaDialog(true),
        },
        {
            label: t("business_customers.detect_and_add"),
            icon: "pi-search-plus",
            show: hasMembershipCriteria,
            onclick: handleDetectAndAdd,
            loading: applyingAudienceCandidates,
        },
        {
            label: t("business_customers.list_to_plan"),
            icon: "pi-send",
            show: canInviteOrSegment,
            onclick: () => setVisibleInvite(true),
        },
        {
            label: t("business_customers.apply_segment"),
            icon: "pi-users",
            show: canInviteOrSegment,
            onclick: () => setShowSegmentPopup(true),
        },
    ];

    if (!defMembership || (fetchingAudience && !audience && !location.state?.audience)) {
        return <Loading />;
    }

    const activityTabs = [
        { label: "business_customers.audience_members" },
        { label: "business_customers.audience_imported_entries" },
        { label: "business_customers.audience_plan_invitations" },
    ];

    const getDisplayColumns = (columns, tabIndex) => {
        if (!isMobileStack) {
            return columns;
        }

        const mobileSummaries = {
            0: membersMobileSummary,
            2: planInvitationsMobileSummary,
        };

        return [{
            field: "mobileSummary",
            header: t(activityTabs[tabIndex]?.label),
            body: mobileSummaries[tabIndex],
            className: "audience-detail-mobile-summary-column",
        }];
    };

    const tableLoading = activeTab === 0
        ? fetchingAudienceMembers
        : activeTab === 1
            ? fetchingEntries
            : fetchingListPlans;

    return (
        <div className="planDetailScreen plan-detail-screen audience-detail-screen">
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                onDismiss={() => setToastTrigger(false)}
                handleActionsAfterToast={handleResendSuccessAfterToast}
                handleActionsAfterErrorToast={() => setToastTrigger(false)}
            />

            <AddAudienceMemberDialog
                visible={showAddMemberDialog}
                onHide={() => setShowAddMemberDialog(false)}
                audienceId={audienceId}
                existingMemberBcIds={existingMemberBcIds}
                onMemberAdded={() => {
                    getAudience(audienceId);
                    getAudienceMembers(audienceId, {
                        page: membersPage.page,
                        size: membersPage.size,
                        sort: membersPage.sort,
                    });
                }}
            />

            <SpecifyCriteriaDialog
                visible={showCriteriaDialog}
                onHide={() => setShowCriteriaDialog(false)}
                audienceId={audienceId}
                businessId={defMembership?.business?.id}
                initialSegmentIds={initialCriteriaSegmentIds}
                initialTagIds={initialCriteriaTagIds}
                onSaved={() => {
                    fetchAudienceCriteria();
                    getAudience(audienceId);
                }}
            />

            <RemoveAudienceMemberDialog
                visible={showRemoveMemberDialog}
                onHide={() => {
                    setShowRemoveMemberDialog(false);
                    setSelectedMember(null);
                }}
                audienceId={audienceId}
                memberId={selectedMember?.memberId}
                memberName={selectedMember?.name}
                onRemoved={() => {
                    getAudience(audienceId);
                    fetchAudienceMembers();
                }}
            />

            <SubscriberToSegment
                visible={showSegmentPopup}
                header={t("segment.add_list_header")}
                onHide={() => setShowSegmentPopup(false)}
                saveLabel={t("segment.add_subs")}
                csvFileData={audienceData}
                fetchAllImportedCustomers={() => getAudience(audienceId)}
            />

            <ConfirmDialog
                header={t("business_customers.resend_invite")}
                visible={resendVisible}
                style={{ width: "350px" }}
                className="resend_"
                onHide={() => setResendVisible(false)}
                message={<Trans i18nKey="business_customers.resend_message" components={{ 1: <span className="reveknew_red font-bold" /> }} />}
                footer={
                    <div>
                        <Button id="clear_segment_btn" onClick={() => setResendVisible(false)}>
                            <i className="pi pi-times mr-1" />
                            {t("cancel")}
                        </Button>
                        <Button
                            id="send_invite_btn"
                            onClick={handleResend}
                            disabled={resendClicked}
                            loading={fetchingBulkPlanInviteResend}
                        >
                            {t("business_customers.proceed_button")}
                        </Button>
                    </div>
                }
            />

            <InviteSubscriber
                visible={visibleInvite}
                toggleVisible={visibleInvite}
                setVisibleInvite={setVisibleInvite}
                header={t("invitation.invite_to_list")}
                onHide={() => setVisibleInvite(false)}
                initialTier={null}
                fromSubscriberImport={visibleInvite}
                csvFileData={audienceData}
                sendBulkInvite={sendBulkPlanInvites}
                makingBulkInvite={fetchingBulkPlanInvite}
                bulkInviteSuccess={bulkPlanInvite}
                fetchAllImportedCustomers={() => getCustomerListInvitedPlans(audienceId)}
            />

            <AudienceDetailCards
                t={t}
                audience={audienceData}
                defMembership={defMembership}
                sideMenu={sideMenu}
                audienceBatches={audienceBatches}
                applyingBatchNos={applyingBatchNos}
                candidateSegments={candidateSegments}
                candidateTags={candidateTags}
                criteriaLoading={fetchingAudienceCandidateSegments || fetchingAudienceCandidateTags}
                displayMemberCount={displayMemberCount}
            />

            {activeTab === 2 && planInvitations.length > 0 && (
                <div className="rowExpansionTemplate">
                    <div className="flex align-items-center justify-content-end">
                        <p className="bulk-button" onClick={handleRefreshPlanInvitations}>
                            {t("business_customers.load-bulk")}
                        </p>
                    </div>
                </div>
            )}

            <CustomDataTable
                key={`audience-detail-${activeTab}-${isMobileStack}`}
                value={activeTab === 0 ? memberRows : activeTab === 2 ? planInvitations : []}
                columns={
                    activeTab === 0
                        ? getDisplayColumns(columnsMembers, 0)
                        : activeTab === 2
                            ? getDisplayColumns(planInvitationColumns, 2)
                            : []
                }
                tabs={activityTabs}
                setActiveTab={setActiveTab}
                activeTab={activeTab}
                setState={setTableState}
                tableClassName="tier_detail_data_table"
                title="business_customers.audience_activity"
                tabHeaderAction={activeTab === 0 ? (
                    <Button
                        className="segment_button import_button"
                        icon="pi pi-plus-circle"
                        label={t("business_customers.search_and_add")}
                        onClick={() => setShowAddMemberDialog(true)}
                    />
                ) : null}
                tabBodyContent={(tabIndex) => (
                    tabIndex === 1 ? (
                        <div className="audience-csv-tab-refresh">
                            {refreshingCsvAfterApply && (
                                <div className="audience-csv-tab-loader">
                                    <Loading />
                                </div>
                            )}
                            <AudienceCsvEntriesAccordion
                                entries={importedEntries}
                                columns={columnsEntriesInBatch}
                                loading={fetchingEntries}
                                isMobileStack={isMobileStack}
                                renderMobileSummary={renderMobileSummary}
                                onProcessBatch={handleApplyBatch}
                                batches={audienceBatches}
                                batchesLoading={fetchingAudienceBatches}
                                applyingBatchNos={applyingBatchNos}
                                t={t}
                            />
                        </div>
                    ) : null
                )}
                rows={20}
                loading={tableLoading}
                tab={tableState.tab}
                responsiveLayout="stack"
                scrollable={!isMobileStack && activeTab !== 1}
                scrollHeight={isMobileStack || activeTab === 1 ? undefined : "flex"}
            />

            {activeTab === 0 && (
                <Paginator
                    className="custom-paginator"
                    rows={membersPage.size}
                    totalRecords={audienceMembers?.totalElements ?? 0}
                    first={membersPage.page * membersPage.size}
                    onPageChange={(e) => {
                        setMembersPage((prev) => ({ ...prev, page: e.page }));
                    }}
                />
            )}

            {audienceError && !audienceData && (
                <p className="audience-error mt-3">{t("err")}</p>
            )}
        </div>
    );
};

export default AudienceDetail;
